"use client"

import {
  addEventSubscriber,
  deleteEventSubscriber,
  fetchEventSubscriberId,
  fetchEventSubscribers,
  updateEventSubscriber,
} from "@/(admin)/modules/subscriber/services/eventSubscriberService"
import { useCheckPermission } from "@/hook/use-check-permission"
import { t } from "@/languages/i18n"
import { PaginationMeta } from "@/types/event"
import {
  EventSubscriberData,
  EventSubscriberFieldType,
  EventSubscriberPayload,
  EventSubscriberType,
} from "@/types/types"
import { generateDuplicateName } from "@/utils/generate-duplicate-name"
import { App, FormInstance } from "antd"
import { useRouter } from "next/navigation"
import queryString from "query-string"
import { useCallback, useState } from "react"

export const useEventSubscribers = () => {
  const router = useRouter()
  const { message, modal } = App.useApp()

  const [dataItem, setDataItem] = useState<EventSubscriberData[]>([])
  const [loading, setLoading] = useState(false)
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([])
  const [bulkAction, setBulkAction] = useState<string>("")
  const [needRefresh, setNeedRefresh] = useState<boolean>(false)
  const [pagination, setPagination] = useState<PaginationMeta>({
    page: 1,
    pageSize: 20,
    total: 0,
    pageCount: 0,
  })

  const { hasPermission, permissions } = useCheckPermission()

  const fetchEventSubscribersData = useCallback(
    async (searchTerms?: any) => {
      setLoading(true)
      try {
        const params = {
          page: String(pagination.page),
          pageSize: String(pagination.pageSize),
          ...searchTerms,
        }
        const response = await fetchEventSubscribers(params)
        if (response && response.data) {
          setDataItem(response.data)
          if (response.meta?.pagination) {
            setPagination(response.meta.pagination)
          }
        } else {
          setDataItem([])
        }
      } catch (error) {
        message.error(t("Failed to fetch data"))
      } finally {
        setLoading(false)
      }
    },
    [message, pagination.page, pagination.pageSize],
  )

  const fetchEventSubscriberByIdAndSetForm = useCallback(
    async (id: number, form?: FormInstance) => {
      try {
        const response = await fetchEventSubscriberId(id)
        if (!response || !response.attributes) {
          message.error(t("Không tìm thấy đối tác sự kiện"))
          return { subscriber: null, formData: null }
        }

        const attr = response.attributes
        const formData: EventSubscriberFieldType = {
          name: attr.name || "",
          email: attr.email || "",
          phone: attr.phone || "",
          type: (attr.type as EventSubscriberType) || EventSubscriberType.EVENT,
          resolved: attr.resolved ?? false,
          event: attr.event?.data?.id || undefined,
        }

        if (form) form.setFieldsValue(formData)
        return { subscriber: response, formData }
      } catch (error) {
        message.error(t("ERROR FETCHING EVENT SUBSCRIBER"))
        return { subscriber: null, formData: null }
      }
    },
    [message],
  )

  const handleUpsertEventSubscriber = useCallback(
    async (values: EventSubscriberFieldType, id?: number) => {
      const payload: EventSubscriberPayload = {
        data: {
          name: values.name,
          email: values.email,
          phone: values.phone,
          type: values.type,
          resolved: !!values.resolved,
          event: values.event as string | number,
        },
      }

      try {
        if (id) {
          await updateEventSubscriber(id, payload)
          message.success(t("Cập nhật đối tác sự kiện thành công!"))
          return { success: true, id }
        } else {
          const res = await addEventSubscriber(payload)
          return { success: true, id: res.data.id }
        }
      } catch {
        message.error(t("Lưu thất bại. Vui lòng thử lại."))
        return { success: false }
      }
    },
    [message],
  )

  const handleDuplicate = useCallback(
    async (subscriber: EventSubscriberData, currentParams?: any) => {
      try {
        setLoading(true)
        const existingNames = dataItem.map(item => item.attributes.name).filter((name): name is string => !!name)

        const duplicatedData: EventSubscriberFieldType = {
          name: generateDuplicateName(subscriber.attributes.name || "", existingNames),
          email: subscriber.attributes.email,
          phone: subscriber.attributes.phone,
          type: subscriber.attributes.type,
          resolved: subscriber.attributes.resolved ?? false,
          event: subscriber.attributes.event?.data?.id || undefined,
        }

        const result = await handleUpsertEventSubscriber(duplicatedData)
        if (result.success) {
          fetchEventSubscribersData(currentParams)
          message.success(t("Nhân bản đối tác sự kiện thành công"))
        }
      } catch (error) {
        message.error(t("Không thể nhân bản đối tác sự kiện"))
      } finally {
        setLoading(false)
      }
    },
    [dataItem, handleUpsertEventSubscriber, fetchEventSubscribersData, message],
  )

  const handleDelete = useCallback(
    async (id: number, currentParams?: any) => {
      modal.confirm({
        title: t("Xác nhận xóa"),
        content: t("Bạn có chắc chắn muốn xóa đối tác sự kiện này?"),
        onOk: async () => {
          try {
            await deleteEventSubscriber(id)
            fetchEventSubscribersData(currentParams)
            message.success(t("Xóa đối tác sự kiện thành công!"))
          } catch {
            message.error(t("Xóa thất bại"))
          }
        },
      })
    },
    [fetchEventSubscribersData, message],
  )

  const handleBulkDelete = useCallback(
    async (currentParams?: any) => {
      try {
        setLoading(true)
        const ids = selectedRowKeys.map(id => Number(id))
        await Promise.all(ids.map(id => deleteEventSubscriber(id)))
        setSelectedRowKeys([])
        fetchEventSubscribersData(currentParams)
        message.success(t("Xóa nhiều đối tác sự kiện thành công"))
      } catch {
        message.error(t("Xóa nhiều thất bại"))
      } finally {
        setLoading(false)
      }
    },
    [selectedRowKeys, fetchEventSubscribersData, message],
  )

  const handleApplyBulkAction = useCallback(
    (currentParams?: any) => {
      if (!hasPermission("subscriber", "delete")) {
        message.error(t("Bạn không có quyền chọn những đối tác sự kiện này"))
        return
      }

      if (bulkAction === "delete" && selectedRowKeys.length > 0) {
        modal.confirm({
          title: t("Xác nhận xóa"),
          content: t("Bạn có chắc chắn muốn xóa những đối tác sự kiện này?"),
          onOk: () => handleBulkDelete(currentParams),
        })
      } else if (selectedRowKeys.length === 0) {
        message.warning(t("Vui lòng chọn đối tác để áp dụng hành động"))
      } else {
        message.warning(t("Vui lòng chọn hành động có sẵn"))
      }
    },
    [bulkAction, selectedRowKeys, handleBulkDelete, message, permissions],
  )

  const handleRefresh = useCallback((filterRef?: { current: { filterFunction: () => void } | null }) => {
    if (filterRef?.current) {
      filterRef.current.filterFunction()
    }
    setNeedRefresh(prev => !prev)
  }, [])

  const handleSearch = useCallback(
    (filters: object, pathname: string) => {
      const searchParams = queryString.stringify(filters, { encode: false })
      router.push(searchParams ? `${pathname}?${searchParams}` : pathname)
    },
    [router],
  )

  return {
    dataItem,
    loading,
    pagination,
    selectedRowKeys,
    bulkAction,
    needRefresh,
    actions: {
      fetchEventSubscribersData,
      fetchEventSubscriberByIdAndSetForm,
      handleDelete,
      handleSearch,
      handleUpsertEventSubscriber,
      setSelectedRowKeys,
      setBulkAction,
      handleApplyBulkAction,
      handleRefresh,
      handleDuplicate,
      hasPermission,
    },
  }
}
